import Tick from '@atlaskit/icon/core/status-success';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import {
  getDatabaseContainerType,
  type InlineCommentEntryPoint,
  useAnalytics
} from '@databases/analytics';
import { getCloudId, useDatabaseContent } from '@databases/database-state';
import { useDialog } from '@databases/dialog';
import { useFlags } from '@databases/flags';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseChangeType } from '@databases/types/redux';
import { getValueId, generateId } from '@databases/utils/utils';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

import { currentHostFrameContext } from '@canvas/canvas-state/host-api';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { CommentContentType } from '@canvas/comments/comment-types';
import { type CommentThreadEvent } from '@canvas/comments/comment/store';
import { getCommentStore } from '@canvas/comments/state/comment-store';
import { getReactionsStore } from '@canvas/comments/state/reactions-store';
import {
  CommentThreadDialogLazy,
  type CommentThreadDialogLazyProps
} from '@canvas/comments/thread/dialog';
import {
  type CommentBaseProps,
  type ThreadNavigationOpts
} from '@canvas/comments/thread/types';
import { useThread } from '@canvas/comments/thread/use-thread';

import { useCommentActionsContext } from '../hooks/useCommentContext.ts';
import { messages } from '../i18n.ts';
import { type ThreadWithValueId } from '../type.ts';

type CommonDialogProps = Omit<CommentBaseProps, 'onCancelled'> & {
  threadId: string;
  verticalPadding: number;
  showCloseButton?: boolean;
  showChildrenComments?: boolean;
  showProfileCard?: boolean;
  cloudId: string | undefined;
  contentId: string;
  accountId: string | undefined;
  activationId?: string;
  threadNavigationOpts?: ThreadNavigationOpts;
  contentType?: CommentContentType;
  originalSelection?: string;
};
interface CommentThreadProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  commentToggled: boolean;
  thread: ThreadWithValueId;
  currentUserId: string | undefined;
  isCreate: boolean;
  commentEntryPoint?: InlineCommentEntryPoint;
  threadNavigationOpts?: ThreadNavigationOpts;
  stringRepresentation?: string;
  setIsDeleting: (isDeleting: boolean) => void;
  onThreadCreate: (evt: CommentThreadEvent) => void;
  handleClose: () => void;
}

/**
 * @deprecated use NewCommentThread component for the Consolidated Comments project instead
 * See: https://hello.atlassian.net/wiki/spaces/Comments/pages/5061707298
 *
 * Gated with feature flag `consolidatedComments` in CommentThreads
 */
const CommentThread: React.FC<CommentThreadProps> = ({
  contentId,
  entryId,
  fieldId,
  commentToggled,
  thread,
  currentUserId,
  isCreate,
  commentEntryPoint,
  threadNavigationOpts,
  stringRepresentation,
  setIsDeleting,
  onThreadCreate,
  handleClose
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const { parentContentId, activationId } = useDatabaseContent();
  const store = getCommentStore(undefined, currentHostFrameContext);
  const reactionsStore = getReactionsStore();
  const cloudId = getCloudId();
  const client = useAnalytics();
  const { setOpenThread, setFocusedCommentThreadId } = useCommentActionsContext();
  const { showDialog } = useDialog();
  const { formatMessage } = useIntl();
  const { showFlag, addFlag, clearFlags, hideFlag } = useFlags();

  const [hasLocalChange, setHasLocalChange] = useState(false);

  const reactionsContentId = parentContentId ?? contentId;

  const comments = useThread(
    store,
    thread?.threadId as string,
    thread?.threadId,
    [],
    cloudId,
    reactionsContentId,
    CommentContentType.DATABASE,
    activationId
  );

  const onThreadUpdate = useCallback(() => {
    if (thread && thread.threadId) {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.INCREMENT_VALUE_COMMENT_THREAD_VERSION_NUMBER,
          fieldId,
          entryId,
          threadId: thread.threadId
        })
      );
    }
  }, [contentId, dispatch, entryId, fieldId, thread]);

  useEffect(() => {
    if (thread && thread.threadId && !hasLocalChange) {
      store.reloadThread(thread.threadId, thread.threadId);
    }

    // close dialog if thread was resolved or deleted remotely
    if (thread?.isResolved || (!thread?.threadId && comments.length > 0)) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.versionNumber]);

  useLayoutEffect(() => {
    if (!store) {
      return;
    }

    const subscriber = (evt: CommentThreadEvent) => {
      if (evt.type === 'threadCreated') {
        onThreadCreate(evt);
      } else if (
        evt.type === 'deletedReplyFromThread' ||
        evt.type === 'addedReplyToThread' ||
        evt.type === 'rootCommentChanged' // thread was resolved
      ) {
        onThreadUpdate();
      } else if (evt.type === 'threadChangedLocally') {
        setHasLocalChange(true);
      }
    };

    if (!commentToggled) {
      store.unsubscribe(subscriber);
      return;
    }

    store.subscribe(subscriber);

    return () => store.unsubscribe(subscriber);
  }, [
    store,
    contentId,
    reactionsContentId,
    entryId,
    fieldId,
    dispatch,
    commentToggled,
    thread?.threadId,
    thread?.isResolved,
    thread,
    onThreadUpdate,
    setOpenThread,
    onThreadCreate,
    cloudId
  ]);

  const handleDelete = (externalId: string) => {
    if (thread?.threadId) {
      setIsDeleting(true);
      showDialog('ConfirmDialog', {
        heading: formatMessage(
          messages['comments-confirm-dialog.delete-comment-heading']
        ),
        body: (
          <>{formatMessage(messages['comments-confirm-dialog.delete-comment-body'])}</>
        ),
        yesLabel: formatMessage(
          messages['comments-confirm-dialog.delete-comment-confirm-button']
        ),
        noLabel: formatMessage(
          messages['comments-confirm-dialog.delete-comment-cancel-button']
        ),
        appearance: 'warning',
        onClose: (result) => {
          if (result) {
            store.deleteComment(thread.threadId, externalId);
            onThreadUpdate();

            dispatch(
              addChangeToDraft(contentId, {
                type: DatabaseChangeType.DECREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT,
                entryId,
                fieldId,
                threadId: thread.threadId
              })
            );

            // deleting head of thread - delete option only present in head if length === 1
            if (thread.commentCount === 1) {
              dispatch(
                addChangeToDraft(contentId, {
                  type: DatabaseChangeType.REMOVE_VALUE_COMMENT_THREAD,
                  entryId,
                  fieldId,
                  threadId: externalId
                })
              );

              handleClose();
            }
          }
          setIsDeleting(false);
        }
      });
    }
  };

  const handleResolve = () => {
    if (thread) {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_VALUE_COMMENT_THREAD_RESOLVED,
          entryId,
          fieldId,
          threadId: thread.threadId,
          isResolved: true
        })
      );
      onThreadUpdate();

      const flagMessage = formatMessage(
        messages['comments-snackbar.confirm-resolved-comment']
      );
      const flagId = `comment-resolved-${thread.threadId}`;
      const undoAction = {
        content: formatMessage(messages['comments-snackbar.undo-resolved-comment']),
        onClick: () => {
          store.reOpenComment(thread.threadId, thread.threadId);
          dispatch(
            addChangeToDraft(contentId, {
              type: DatabaseChangeType.SET_VALUE_COMMENT_THREAD_RESOLVED,
              entryId,
              fieldId,
              threadId: thread.threadId,
              isResolved: false
            })
          );
          setOpenThread({
            valueId: getValueId(entryId, fieldId),
            threadId: thread.threadId
          });
          if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
            hideFlag(flagId);
          } else {
            clearFlags();
          }
        }
      };

      if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
        showFlag({
          id: flagId,
          title: flagMessage,
          type: 'success-circle',
          close: 'auto',
          actions: [undoAction]
        });
      } else {
        addFlag({
          title: flagMessage,
          description: '',
          appearance: 'normal',
          icon: () => (
            <Tick spacing="spacious" label="" color={token('color.icon.success')} />
          ),
          actions: [undoAction]
        });
      }
      handleClose();
    }
  };

  const onReply = useCallback(
    (externalId?: string, parentContentId?: string) => {
      if (!thread) {
        return;
      }

      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.INCREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT,
          entryId,
          fieldId,
          threadId: thread.threadId
        })
      );

      if (externalId && parentContentId) {
        client.sendDatabaseCommentCreatedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          commentId: externalId,
          parentCommentContentId: parentContentId
        });
      }

      onThreadUpdate();
    },
    [client, contentId, dispatch, entryId, fieldId, onThreadUpdate, thread]
  );

  const onClose = useCallback(() => {
    setFocusedCommentThreadId(undefined);
  }, [setFocusedCommentThreadId]);

  const commonDialogProps: CommonDialogProps = {
    onDelete: handleDelete,
    onResolve: handleResolve,
    onReOpen: () => () => {},
    onReply: onReply,
    onClick: () => () => {},
    onEdit: () => () => {},
    onEditCancel: () => () => {},
    onCreated: (_: string, commentContentId?: string) => {
      if (commentContentId) {
        client.sendDatabaseCommentCreatedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          commentId: commentContentId,
          commentEntryPoint
        });
      }
    },
    canCreate: true,
    store,
    reactionsStore,
    threadId: thread?.threadId ?? generateId(),
    comments,
    showCloseButton: true,
    showChildrenComments: true,
    showProfileCard: true,
    verticalPadding: 0,
    cloudId,
    contentId: reactionsContentId,
    accountId: currentUserId,
    activationId,
    contentType: CommentContentType.DATABASE,
    originalSelection: stringRepresentation,
    threadNavigationOpts: threadNavigationOpts && threadNavigationOpts
  };

  const properties: CommentThreadDialogLazyProps = !thread?.threadId
    ? {
        type: 'unpersisted',
        onClose: onClose,
        onCancelled: () => {},
        ...commonDialogProps
      }
    : {
        type: 'persisted',
        externalId: thread.threadId,
        onClose: onClose,
        onCancelled: () => {},
        ...commonDialogProps
      };

  // If a comment should have content but does not yet, we render nothing. This
  // will avoid UI jank in the interim. Can be used in future for a loading state.
  if (!thread?.threadId && comments.length === 0 && !isCreate) {
    return null;
  }

  return (
    <Suspense fallback={<Spinner interactionName="load" />}>
      <CommentThreadDialogLazy {...properties} onClose={handleClose} />
    </Suspense>
  );
};

export default CommentThread;
